import { CardData, CardType, ContentMediumType, DeckObject } from "../../constants/GlobalTypesAndEnumsAndConstants";

export type ResolvedCardSourceContext = {
  contentMediumType: ContentMediumType | null;
  pageUrl: string | null;
  location: number | null;
  locationSource: "retrieval" | "generation" | null;
};

// Cloudflare's origin timeout is 524 rather than the standard gateway 504.
const TRANSIENT_CARD_SOURCE_CONTEXT_RESPONSE_STATUSES = new Set([408, 429, 500, 502, 503, 504, 524]);
const CARD_SOURCE_CONTEXT_MAX_REQUEST_ATTEMPTS = 2;
const CARD_SOURCE_CONTEXT_RETRY_DELAY_MS = 300;

export type CardSourceContextRequestResult =
  | {
      response: Response;
      error: null;
      attemptCount: number;
    }
  | {
      response: null;
      error: unknown;
      attemptCount: number;
    };

/**
 * Retry one transient source-context request.
 *
 * Modal normally answers this lookup in one or two seconds, but an overloaded
 * request can occasionally reach its upstream timeout before the body is read.
 * One bounded retry recovers that race without repeatedly running embeddings
 * for deterministic client errors.
 */
export async function requestCardSourceContextWithRetry({
  makeRequest,
  waitBeforeRetry = () =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, CARD_SOURCE_CONTEXT_RETRY_DELAY_MS);
    }),
}: {
  makeRequest: () => Promise<Response>;
  waitBeforeRetry?: () => Promise<void>;
}): Promise<CardSourceContextRequestResult> {
  for (let attemptCount = 1; attemptCount <= CARD_SOURCE_CONTEXT_MAX_REQUEST_ATTEMPTS; attemptCount += 1) {
    try {
      const response = await makeRequest();
      const shouldRetry =
        TRANSIENT_CARD_SOURCE_CONTEXT_RESPONSE_STATUSES.has(response.status) &&
        attemptCount < CARD_SOURCE_CONTEXT_MAX_REQUEST_ATTEMPTS;
      if (!shouldRetry) {
        return { response, error: null, attemptCount };
      }
    } catch (error) {
      if (attemptCount === CARD_SOURCE_CONTEXT_MAX_REQUEST_ATTEMPTS) {
        return { response: null, error, attemptCount };
      }
    }
    await waitBeforeRetry();
  }

  // The bounded loop always returns. Keep a defensive result so callers never
  // have to handle an impossible undefined value if the constants change.
  return {
    response: null,
    error: new Error("Card source context request exhausted without a response"),
    attemptCount: CARD_SOURCE_CONTEXT_MAX_REQUEST_ATTEMPTS,
  };
}

function parseSourceLocation(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && value.trim() === "") return null;

  const parsedValue = typeof value === "number" ? value : Number(value.trim());
  if (!Number.isInteger(parsedValue) || parsedValue < 0) return null;
  return parsedValue;
}

/**
 * Resolve the location shown to the learner.
 *
 * Retrieval-derived `location_context` remains the preferred locator. Newer
 * question-generation records also retain the page/second selected by the
 * generation model. That value is a safe fallback when the later retrieval
 * lookup has not run or returned no match.
 */
export function resolveCardSourceLocation(card: CardData | null | undefined): {
  location: number | null;
  source: "retrieval" | "generation" | null;
} {
  if (!card) return { location: null, source: null };

  const retrievalLocation = parseSourceLocation(card.location_context);
  if (retrievalLocation !== null) {
    return {
      // The PDF viewer is one-indexed. Treat a malformed legacy zero as the
      // first page instead of hiding the source control.
      location: card.content_medium_type === ContentMediumType.PDF ? Math.max(1, retrievalLocation) : retrievalLocation,
      source: "retrieval",
    };
  }

  const generationLocations = Array.isArray(card.location_context_from_llm)
    ? card.location_context_from_llm
    : [card.location_context_from_llm];
  for (const generationLocationValue of generationLocations) {
    const generationLocation = parseSourceLocation(generationLocationValue);
    if (generationLocation === null) continue;
    return {
      location:
        card.content_medium_type === ContentMediumType.PDF ? Math.max(1, generationLocation) : generationLocation,
      source: "generation",
    };
  }

  return { location: null, source: null };
}

/**
 * Card documents and deck generation metadata were historically populated at
 * different times. Resolve each field independently so a partial card record
 * can still use the canonical deck source.
 */
export function resolveCardSourceContext({
  card,
  deck,
  preferCard = true,
}: {
  card?: CardData | null;
  deck?: DeckObject | null;
  preferCard?: boolean;
}): ResolvedCardSourceContext {
  const deckContentMediumType = deck?.generation_metadata?.content_medium_type ?? null;
  const deckPageUrl = deck?.generation_metadata?.page_url || null;
  const contentMediumType = preferCard
    ? (card?.content_medium_type ?? deckContentMediumType)
    : (deckContentMediumType ?? card?.content_medium_type ?? null);
  const pageUrl = preferCard ? card?.page_url || deckPageUrl : deckPageUrl || card?.page_url || null;
  const resolvedLocation = resolveCardSourceLocation(card);
  const normalizedLocation =
    contentMediumType === ContentMediumType.PDF && resolvedLocation.location !== null
      ? Math.max(1, resolvedLocation.location)
      : resolvedLocation.location;

  return {
    contentMediumType,
    pageUrl,
    location: normalizedLocation,
    locationSource: resolvedLocation.source,
  };
}

export function isSourceViewerSupported({
  contentMediumType,
  pageUrl,
}: Pick<ResolvedCardSourceContext, "contentMediumType" | "pageUrl">): boolean {
  return (
    !!pageUrl &&
    [
      ContentMediumType.PDF,
      ContentMediumType.YouTubeVideo,
      ContentMediumType.Webpage,
      ContentMediumType.Image,
    ].includes(contentMediumType)
  );
}

export function getMediaSourceStartSecond(sourceSecond: number): number {
  return Math.max(sourceSecond - 5, 0);
}

/**
 * The generation-time locator can render a source button immediately, but it
 * does not replace the retrieval lookup: chat grounding still needs the
 * relevant source excerpt. Keep those concerns independent.
 */
export function shouldRetrieveCardSourceContext(card: CardData | null | undefined): boolean {
  if (!card?.id || card.relevant_context_for_question) return false;
  return (
    !card.is_custom_card &&
    !card.did_attempt_to_get_relevant_context_for_question &&
    card.card_type !== CardType.ImageOcclusion &&
    card.content_medium_type !== ContentMediumType.AnkiTxt &&
    card.content_medium_type !== ContentMediumType.Webpage &&
    card.content_medium_type !== ContentMediumType.Image
  );
}
